import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LogoSVG } from '../../components/Icons';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, where, getDoc } from 'firebase/firestore';
import { auth, functions } from '../../utils/firebase';
import { db } from '../../utils/firestore';
import './AsistenteLegal.css';

interface Mensaje { rol: 'user' | 'assistant'; texto: string; fuentes?: string[]; documentoNombre?: string; }
interface Conversacion {
  id: string; titulo: string; fecha: string; mensajes: Mensaje[];
  usuarioId?: string; usuarioEmail?: string;
  // Documento de trabajo activo de la conversación. Se persiste para que
  // sobreviva a recargas y esté disponible en cada turno (no solo cuando se adjunta).
  docNombre?: string; docTexto?: string;
}

const EJEMPLOS = [
  `TUTELA LABORAL — Vulneración de garantía de indemnidad. Trabajadora María González, RUT 12.345.678-9, se desempeñaba como vendedora en Retail S.A. desde marzo 2021, sueldo base $520.000 + comisiones promedio $380.000. En octubre 2024 interpuso denuncia ante la Inspección del Trabajo por incumplimiento de jornada. Dos semanas después fue despedida bajo causal necesidades de la empresa. Solicitar tutela con nulidad del despido, reincorporación o indemnización sustitutiva, más recargo del 80%, daño moral $5.000.000.`,
  `NULIDAD DEL DESPIDO por no pago de cotizaciones previsionales. Trabajador Pedro Muñoz, RUT 9.876.543-2, operario en Constructora del Sur Ltda., 8 años de servicio continuos, último sueldo $850.000. Despedido el 15 de enero de 2025 por causal art. 161 CT. Empleador mantiene deuda previsional en AFP Habitat y Fonasa por los últimos 14 meses. Solicitar nulidad del despido, pago de remuneraciones durante periodo de separación, indemnización por años de servicio con recargo 50%, feriado proporcional y horas extras no pagadas estimadas en $2.400.000.`,
  `ALIMENTOS DEFINITIVOS — Madre: Claudia Pérez, RUT 15.234.567-8, trabaja como auxiliar de enfermería, sueldo $480.000, arrienda en $220.000, tiene a cargo dos hijos: Sofía (7 años) y Martín (4 años). Padre: Roberto Silva, RUT 11.222.333-4, trabaja como técnico en empresa minera, sueldo acreditado $2.100.000, vive con conviviente sin hijos. No paga alimentos desde agosto 2024. Solicitar alimentos definitivos equivalentes al 40% del ingreso del padre, más pensión de alimentos provisorios urgentes mientras dura el juicio.`,
  `DEMANDA LABORAL — Cobro de prestaciones. Trabajadora Carla Rojas, RUT 16.789.012-3, ejerció como asistente administrativa en Clínica Dental SpA por 3 años y 4 meses, sueldo $680.000. Empresa le exigía firmar liquidaciones con montos distintos a los reales y trabajar sábados sin pago de horas extras (aproximadamente 6 horas semanales). Fue despedida verbalmente el 2 de diciembre de 2024 sin carta de aviso ni pago de indemnizaciones. Solicitar: indemnización sustitutiva del aviso, indemnización por años de servicio, feriado proporcional, horas extraordinarias de los últimos 2 años, diferencias de remuneraciones y nulidad del despido.`,
];

const nuevoId = () => Date.now().toString(36) + Math.random().toString(36).slice(2);
const COLL = 'chatHistorial';

// Firestore limita cada documento a ~1 MB. Toda la conversación se guarda en un
// solo documento, por lo que tras varios escritos largos el guardado fallaría.
// Recortamos los mensajes más antiguos para mantenernos por debajo del límite.
const LIMITE_FIRESTORE_BYTES = 900_000;
const recortarParaFirestore = (msgs: Mensaje[]): Mensaje[] => {
  const tam = (arr: Mensaje[]) => new Blob([JSON.stringify(arr)]).size;
  const out = [...msgs];
  while (out.length > 2 && tam(out) > LIMITE_FIRESTORE_BYTES) {
    out.shift(); // elimina el mensaje más antiguo
  }
  return out;
};

const AsistenteLegal: React.FC = () => {
  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [convActualId, setConvActualId]     = useState<string | null>(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(true);
  const [input, setInput]       = useState('');
  const [cargando, setCargando] = useState(false);
  const [indexando, setIndexando] = useState(false);
  const [msgIndex, setMsgIndex] = useState('');
  const [error, setError]       = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mostrarEjemplos, setMostrarEjemplos] = useState(false);
  const [docAdjunto, setDocAdjunto] = useState<{ nombre: string; texto: string } | null>(null);
  const [cargandoDoc, setCargandoDoc] = useState(false);
  const [rolUsuario, setRolUsuario] = useState<string | null>(null);
  const [verificandoRol, setVerificandoRol] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRefChat = useRef<HTMLInputElement>(null);
  const docHidratadoRef = useRef<string | null>(null);

  const convActual = conversaciones.find(c => c.id === convActualId) ?? null;
  const mensajes   = convActual?.mensajes ?? [];

  // Al abrir/cambiar de conversación, recuperar su documento de trabajo persistido
  // (una sola vez por conversación, para no pisar un adjunto recién cargado).
  useEffect(() => {
    if (!convActualId || !convActual) return;
    if (docHidratadoRef.current === convActualId) return;
    docHidratadoRef.current = convActualId;
    setDocAdjunto(
      convActual.docTexto && convActual.docNombre
        ? { nombre: convActual.docNombre, texto: convActual.docTexto }
        : null
    );
  }, [convActualId, convActual]);

  // Verificar rol esperando a que Firebase Auth restaure la sesión
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { setVerificandoRol(false); return; }
      try {
        const snap = await getDoc(doc(db, 'usuarios', user.uid));
        setRolUsuario(snap.data()?.rol ?? null);
      } catch {
        setRolUsuario(null);
      } finally {
        setVerificandoRol(false);
      }
    });
    return () => unsub();
  }, []);

  // Cargar historial: procuradora ve solo sus propias consultas; admin y abogada ven todo
  useEffect(() => {
    if (verificandoRol) return;
    const uid = auth.currentUser?.uid;
    const q = (rolUsuario === 'procuradora' && uid)
      ? query(collection(db, COLL), where('usuarioId', '==', uid))
      : query(collection(db, COLL), orderBy('fecha', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const convs = snap.docs
        .map(d => ({ ...d.data(), id: d.id } as Conversacion))
        .sort((a, b) => b.fecha.localeCompare(a.fecha));
      setConversaciones(convs);
      setCargandoHistorial(false);
      setConvActualId(prev => prev ?? (convs[0]?.id ?? null));
    }, err => {
      console.error('Error cargando historial:', err);
      setCargandoHistorial(false);
    });
    return () => unsub();
  }, [rolUsuario, verificandoRol]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, cargando]);

  const actualizarConv = useCallback(async (id: string, msgs: Mensaje[]) => {
    await updateDoc(doc(db, COLL, id), { mensajes: recortarParaFirestore(msgs), fecha: new Date().toISOString() });
  }, []);

  const crearNuevaConversacion = async () => {
    const id = nuevoId();
    const nueva: Conversacion = {
      id, titulo: 'Nueva consulta', fecha: new Date().toISOString(), mensajes: [],
      usuarioId: auth.currentUser?.uid ?? '', usuarioEmail: auth.currentUser?.email ?? '',
    };
    await setDoc(doc(db, COLL, id), nueva);
    docHidratadoRef.current = id; // conversación nueva sin documento; evita rehidratar
    setConvActualId(id);
    setError('');
    setDocAdjunto(null);
  };

  const handleFileAdjunto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCargandoDoc(true);
    try {
      const base64 = await new Promise<string>((res) => {
        const r = new FileReader();
        r.onload = () => res((r.result as string).split(',')[1]);
        r.readAsDataURL(file);
      });
      const fn = httpsCallable(functions, 'extraerTextoArchivo');
      const result: any = await fn({ fileBase64: base64, mimeType: file.type || 'application/octet-stream', fileName: file.name });
      const nuevoDoc = { nombre: result.data.nombre as string, texto: result.data.texto as string };
      setDocAdjunto(nuevoDoc);
      // Persistir el documento en la conversación para que sobreviva recargas y turnos.
      if (convActualId) {
        docHidratadoRef.current = convActualId;
        await updateDoc(doc(db, COLL, convActualId), { docNombre: nuevoDoc.nombre, docTexto: nuevoDoc.texto });
      }
    } catch (err: any) {
      alert('No se pudo cargar el archivo: ' + (err.message || 'Intenta de nuevo.'));
    } finally {
      setCargandoDoc(false);
      if (e.target) e.target.value = '';
    }
  };

  const eliminarConversacion = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteDoc(doc(db, COLL, id));
    if (convActualId === id) setConvActualId(conversaciones.find(c => c.id !== id)?.id ?? null);
  };

  const enviar = async () => {
    const texto = input.trim();
    if (!texto || cargando) return;
    setInput('');
    setError('');

    let id = convActualId;
    if (!id) {
      id = nuevoId();
      const nueva: Conversacion = {
        id, titulo: texto.slice(0, 60), fecha: new Date().toISOString(), mensajes: [],
        usuarioId: auth.currentUser?.uid ?? '', usuarioEmail: auth.currentUser?.email ?? '',
      };
      await setDoc(doc(db, COLL, id), nueva);
      docHidratadoRef.current = id;
      setConvActualId(id);
    }

    const msgUsuario: Mensaje = { rol: 'user', texto, ...(docAdjunto ? { documentoNombre: docAdjunto.nombre } : {}) };
    const nuevos: Mensaje[] = [...mensajes, msgUsuario];
    // Capturamos el documento adjunto ANTES de cualquier await para evitar que
    // un cambio de estado lo pierda a mitad del envío.
    const adjunto = docAdjunto;
    const updates: any = { mensajes: recortarParaFirestore(nuevos), fecha: new Date().toISOString() };
    if (mensajes.length === 0) updates.titulo = texto.slice(0, 60);
    // Persistir el documento de trabajo en la conversación (sobrevive recargas/turnos).
    if (adjunto?.texto?.trim()) { updates.docNombre = adjunto.nombre; updates.docTexto = adjunto.texto; }
    try {
      await updateDoc(doc(db, COLL, id), updates);
    } catch (e: any) {
      setError('No se pudo guardar el mensaje: ' + (e?.message || 'inténtalo de nuevo.'));
      setInput(texto);
      return;
    }

    setCargando(true);
    try {
      const fn = httpsCallable(functions, 'consultarAsistente', { timeout: 600000 });
      const historial = nuevos.slice(-7).map(m => ({ rol: m.rol, texto: m.texto }));
      const payload: any = { pregunta: texto, historial: historial.slice(0, -1) };
      if (adjunto?.texto?.trim()) { payload.documentoTexto = adjunto.texto; payload.documentoNombre = adjunto.nombre; }
      const res: any = await fn(payload);
      const conRespuesta: Mensaje[] = [...nuevos, { rol: 'assistant', texto: res.data.respuesta, fuentes: res.data.fuentes }];
      await actualizarConv(id, conRespuesta);
    } catch (e: any) {
      setError(e.message || 'Error al conectar con el asistente.');
      await actualizarConv(id, nuevos);
    } finally {
      setCargando(false);
    }
  };

  const indexar = async () => {
    setIndexando(true); setMsgIndex('');
    try {
      const fn = httpsCallable(functions, 'ingestarDocumentos');
      const res: any = await fn({});
      const todos: any[] = res.data.resultados;
      const ok       = todos.filter((r: any) => r.estado.startsWith('ok')).length;
      const errores  = todos.filter((r: any) => r.estado.startsWith('error'));
      const omitidos = todos.filter((r: any) => r.estado.startsWith('omitido')).length;
      let resumen = `✓ ${ok} indexados`;
      if (omitidos) resumen += ` · ${omitidos} omitidos`;
      if (errores.length) {
        resumen += ` · ${errores.length} con error`;
        resumen += ` (${errores.map((r: any) => `${r.name}: ${r.estado}`).join(' | ')})`;
      }
      setMsgIndex(resumen);
    } catch (e: any) {
      setMsgIndex('Error: ' + (e.message || 'No se pudo indexar'));
    } finally { setIndexando(false); }
  };

  const fmtFecha = (iso: string) => {
    const d = new Date(iso);
    const hoy = new Date();
    if (d.toDateString() === hoy.toDateString()) return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  const nombreUsuario = (email?: string) => email ? email.split('@')[0] : '';

  const vacio = mensajes.length === 0;
  const tieneAcceso = rolUsuario === 'admin' || rolUsuario === 'abogada' || rolUsuario === 'procuradora';

  if (verificandoRol) {
    return <div className="al-root" style={{ alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Verificando acceso…</p></div>;
  }
  if (!tieneAcceso) {
    return (
      <div className="al-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
          <h2 style={{ color: '#111827', fontFamily: 'Cinzel, serif', marginBottom: '0.5rem' }}>Acceso restringido</h2>
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>El Redactor Legal IA está disponible solo para abogadas y administración.</p>
          <a href="/" style={{ display: 'inline-block', marginTop: '1.5rem', color: '#1d4ed8', fontSize: '0.85rem' }}>← Volver al inicio</a>
        </div>
      </div>
    );
  }

  return (
    <div className="al-root">
      <header className="al-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="al-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} title="Historial">☰</button>
          <a href="/" className="al-logo">
            <LogoSVG size={32} />
            <div className="al-logo-text">
              <span className="al-logo-name">Souza &amp; Villa</span>
              <span className="al-logo-tag">Redactor Legal IA</span>
            </div>
          </a>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {msgIndex && <span className="al-msg-index-status" style={{ color: msgIndex.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{msgIndex}</span>}
          <button className="al-index-btn" onClick={indexar} disabled={indexando} title="Indexar documentos de Drive">
            {indexando ? '⏳ Indexando…' : '📂 Indexar Drive'}
          </button>
          <span className="al-badge">Solo equipo</span>
        </div>
      </header>

      <div className="al-body">
        {/* ── Sidebar ── */}
        <aside className={`al-sidebar${sidebarOpen ? ' al-sidebar--open' : ''}`}>
          <button className="al-new-btn" onClick={crearNuevaConversacion}>+ Nueva consulta</button>
          <div className="al-conv-list">
            {cargandoHistorial && <p className="al-conv-empty">Cargando historial…</p>}
            {!cargandoHistorial && conversaciones.length === 0 && (
              <p className="al-conv-empty">Sin historial aún</p>
            )}
            {conversaciones.map(conv => (
              <div
                key={conv.id}
                className={`al-conv-item${conv.id === convActualId ? ' al-conv-item--active' : ''}`}
                onClick={() => { setConvActualId(conv.id); setError(''); }}
              >
                <div className="al-conv-titulo">{conv.titulo}</div>
                <div className="al-conv-meta">
                  {conv.usuarioEmail && <span className="al-conv-autor">{nombreUsuario(conv.usuarioEmail)}</span>}
                  <span>{fmtFecha(conv.fecha)}</span>
                </div>
                <button className="al-conv-delete" onClick={e => eliminarConversacion(conv.id, e)} title="Eliminar">×</button>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Chat ── */}
        <main className="al-main">
          <div className="al-scroll-area">
            {vacio ? (
              <div className="al-hero">
                <div className="al-hero-icon">⚖️</div>
                <h1 className="al-title">Redactor Legal IA</h1>
                <p className="al-subtitle">
                  Describe el caso y redactaré el escrito jurídico fundamentado<br />
                  en la base documental del estudio.
                </p>
                <button className="al-ejemplos-toggle" onClick={() => setMostrarEjemplos(v => !v)}>
                  {mostrarEjemplos ? '▲ Ocultar ejemplos' : '▼ Ver ejemplos de casos'}
                </button>
                {mostrarEjemplos && (
                  <div className="al-examples">
                    {EJEMPLOS.map((ex, i) => (
                      <button key={i} className="al-example-btn" onClick={() => { setInput(ex); setMostrarEjemplos(false); }}>{ex}</button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="al-chat">
                {mensajes.map((m, i) => (
                  <div key={i} className={`al-msg al-msg-${m.rol}`}>
                    <div className="al-msg-bubble">
                      {m.documentoNombre && (
                        <div className="al-msg-doc-badge">📄 {m.documentoNombre}</div>
                      )}
                      <div className="al-msg-text"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.texto}</ReactMarkdown></div>
                      {m.fuentes && m.fuentes.length > 0 && (
                        <div className="al-fuentes">
                          <span className="al-fuentes-label">Fuentes consultadas:</span>
                          {m.fuentes.map((f, j) => <span key={j} className="al-fuente-chip">{f}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {cargando && (
                  <div className="al-msg al-msg-assistant">
                    <div className="al-msg-bubble al-loading">✍️ Procesando…</div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}
            {error && <div className="al-error">{error}</div>}
          </div>

          <div className="al-input-area">
            {docAdjunto && (
              <div className="al-attach-bar">
                <span className="al-attach-chip">
                  📄 {docAdjunto.nombre}
                  <button className="al-attach-chip-remove" onClick={async () => { setDocAdjunto(null); if (convActualId) await updateDoc(doc(db, COLL, convActualId), { docNombre: '', docTexto: '' }); }} title="Quitar archivo">✕</button>
                </span>
              </div>
            )}
            <textarea
              className="al-textarea"
              placeholder={docAdjunto ? 'Indícale a la IA qué quieres: corrección, puntos débiles, artículos aplicables, mejoras…' : 'Describe el caso: partes, hechos, pretensión, monto, antecedentes relevantes…'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar(); }}
              rows={3}
            />
            <div className="al-input-footer">
              <button className="al-attach-btn" onClick={() => fileRefChat.current?.click()} disabled={cargandoDoc} title="Adjuntar escrito (PDF, Word, .txt)">
                {cargandoDoc ? '⏳' : '📎 Adjuntar escrito'}
              </button>
              <input
                ref={fileRefChat}
                type="file"
                accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={handleFileAdjunto}
                style={{ display: 'none' }}
              />
              <span className="al-input-hint">Ctrl+Enter para enviar</span>
              <button className="al-send-btn" onClick={enviar} disabled={!input.trim() || cargando}>
                {cargando ? 'Procesando…' : 'Enviar →'}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AsistenteLegal;
